import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { getUserRolesLabel } from "components/MemberList/userRoles";
import type { PClosing, TSettings } from "components/Settings/types.js";
import { ROLES } from "containers/ScopeContext/roles.enum";
import { APP_UNAVAILABLE_MESSAGE, isAppUnavailablePayload, toastApiError } from "helpers/apiErrorToast";
import { base64ImageEncode, getCleanJSON, translationsWithFallback } from "helpers/utils";
import { t } from "i18next";
import { toast } from "react-toastify";
import type { SubsetOf } from "types";
import type { CountryFeatures } from "types/countryFeatures";

import type { ICurrencyOption } from "../currenciesList";
import { store } from "./store";
import type { TFetchStatus, TPortalType } from "./types";

/** `/company` is the call that may lift the screen. A cached copy kept the previous window. */
function isCompanyInfoUrl(url: string, apiEndpoint: string): boolean {
    const companyUrl = `${apiEndpoint.replace(/\/$/, "")}/company`;
    return url.split("?")[0] === companyUrl;
}

axios.interceptors.response.use(
    (response) => {
        if (
            response.config.method === "get" &&
            response.request.responseURL.endsWith("/company") &&
            response.data?.data?.isCurrentlyClosing
        ) {
            store.dispatch(setClosingPeriod({ closing: true }));
        }
        syncMaintenanceFromResponse(response);
        return response;
    },
    (error) => {
        const message = error.response?.data?.message;
        if (message === APP_UNAVAILABLE_MESSAGE) {
            const data = error.response?.data?.data;
            store.dispatch(
                applyMaintenanceSignal({
                    startsAt: readMaintenanceBound(data, "startsAt"),
                    endsAt: readMaintenanceBound(data, "endsAt"),
                    title: readMaintenanceText(data, "title"),
                    description: readMaintenanceText(data, "description"),
                }),
            );
        } else if (message === CLOSING_PERIOD_MESSAGE) {
            store.dispatch(setClosingPeriod({ closing: true }));
        } else if (isBackendUnreachable(error)) {
            const remembered = rememberedActiveWindow();
            if (remembered) store.dispatch(applyMaintenanceSignal(remembered));
        }
        return Promise.reject(error);
    },
);

export const fetchSettings = createAsyncThunk("settings/profile", async (_, { rejectWithValue }) => {
    try {
        const response = await axios.get<TSettings>(import.meta.env.REACT_APP_API_ENDPOINT + "/settings");
        const data = response.data;
        return data;
    } catch (error: any) {
        return rejectWithValue(error.response?.data);
    }
});

// Email templates are read as objects by the composer; an empty or invalid value (e.g. a reset template)
// must not throw inside the reducer.
const parseEmailTemplateContent = (value: string) => {
    const parsed = getCleanJSON(value);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : { subject: "", body: "" };
};

export const fetchCompanyInfo = createAsyncThunk("settings/fetchCompany", async (_, { rejectWithValue }) => {
    try {
        const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + "/company");
        // A 200 without the expected envelope (e.g. an HTML page from a proxy) must not reach the reducer.
        if (!Array.isArray(response.data?.data?.company?.settings)) return rejectWithValue(response.data);
        return response.data.data;
    } catch (error: any) {
        return rejectWithValue(error.response?.data);
    }
});

export const fetchMe = createAsyncThunk("settings/fetchMe", async (_, { rejectWithValue }) => {
    try {
        const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + "/users/me");
        return response.data.data.user;
    } catch (error: any) {
        return rejectWithValue(error.response.data);
    }
});

export const fetchMeBP = createAsyncThunk("settings/fetchMeBP", async (_, { rejectWithValue }) => {
    try {
        const response = await axios.get(import.meta.env.REACT_APP_API_BENEFICIAR_PORTAL_ENDPOINT + "/users/me");
        return response.data.data.user;
    } catch (error: any) {
        return rejectWithValue(error.response.data);
    }
});

export const updateMe = createAsyncThunk(
    "settings/updateMe",
    async ({ jobTitle }: { jobTitle: string }, { rejectWithValue }) => {
        try {
            const response = await axios.put(import.meta.env.REACT_APP_API_ENDPOINT + "/users/me", {
                jobTitle,
            });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const putMe = createAsyncThunk(
    "settings/putMe",
    async ({ companyId }: { companyId: number }, { rejectWithValue }) => {
        try {
            const response = await axios.put(import.meta.env.REACT_APP_API_ENDPOINT + "/users/me/change", {
                companyId,
            });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const putMeVerifiedOrAcceptedTos = createAsyncThunk(
    "settings/putMeVerifiedOrAcceptedTos",
    async ({ isVerified, hasAcceptedToS }: { isVerified: boolean; hasAcceptedToS: boolean }, { rejectWithValue }) => {
        try {
            const response = await axios.put(import.meta.env.REACT_APP_API_ENDPOINT + "/users/me", {
                isVerified,
                hasAcceptedToS,
            });
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const putMeVerifiedOrAcceptedTosBP = createAsyncThunk(
    "settings/putMeVerifiedOrAcceptedTosBP",
    async ({ isVerified, hasAcceptedToS }: { isVerified: boolean; hasAcceptedToS: boolean }, { rejectWithValue }) => {
        try {
            const response = await axios.put(import.meta.env.REACT_APP_API_BENEFICIAR_PORTAL_ENDPOINT + "/users/me", {
                isVerified,
                hasAcceptedToS,
            });
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const patchCompanyInfo = createAsyncThunk(
    "settings/patchCompany",
    async (newCompanyData: any, { rejectWithValue }) => {
        try {
            const settings = Array.isArray(newCompanyData.settings)
                ? structuredClone(newCompanyData.settings)
                : undefined;
            const newSettings = settings?.map((s: any) => {
                if (typeof s.content === "object" && s.content !== null) {
                    return { ...s, content: JSON.stringify(s.content) };
                }
                return s;
            });
            const bankingMetadata = newCompanyData.bankingMetadata;
            const response = await axios.patch(import.meta.env.REACT_APP_API_ENDPOINT + "/company", {
                ...newCompanyData,
                bankingMetadata,
                settings: newSettings,
            });
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue({ ...error.response.data, status: error.response.status });
        }
    },
);

export const putBankInfo = createAsyncThunk("settings/putBankInfo", async (newBankData: any, { rejectWithValue }) => {
    try {
        const response = await axios.put(import.meta.env.REACT_APP_API_ENDPOINT + "/company/bank", newBankData);
        return response.data.data;
    } catch (error: any) {
        return rejectWithValue(error.response.data);
    }
});

export const putCompanySettings = createAsyncThunk(
    "settings/putCompanySettings",
    async (settings: { id?: number; settingsName: string; settingsValue: string }[], { rejectWithValue }) => {
        try {
            const response = await axios.put(import.meta.env.REACT_APP_API_ENDPOINT + "/company/settings", settings);
            return response.data;
        } catch (err: any) {
            console.log(err);
            return rejectWithValue(err.response.data);
        }
    },
);

export const sendCompanySignature = createAsyncThunk(
    "company/sendCompanySignature",
    async (file: File, { rejectWithValue }) => {
        try {
            const base64string = await base64ImageEncode(file);
            const payload = {
                name: file.name,
                mime: file.type,
                size: file.size,
                content: base64string,
            };
            const response = await axios.post<{ imagePath: string }>(
                import.meta.env.REACT_APP_API_ENDPOINT + "/images/upload-base64",
                payload,
            );
            return response.data.imagePath;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchAPIKeys = createAsyncThunk("settings/fetchAPIKeys", async (_, { rejectWithValue }) => {
    try {
        const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + "/company/keys");
        return response.data.data;
    } catch (error: any) {
        return rejectWithValue(error.response.data);
    }
});

export const deleteOneAPIKey = createAsyncThunk(
    "settings/deleteOneAPIKey",
    async (apiKey: APIKey, { rejectWithValue }) => {
        try {
            await axios.delete(import.meta.env.REACT_APP_API_ENDPOINT + "/company/keys/" + apiKey.id);
            return apiKey;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const generateAPIKey = createAsyncThunk(
    "settings/generateAPIKey",
    async (apiKey: Pick<APIKey, "name" | "keyScope" | "expiryDate">, { rejectWithValue }) => {
        try {
            const result = await axios.post(import.meta.env.REACT_APP_API_ENDPOINT + "/company/keys", apiKey);
            return result.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

/**
 * Update Crealo admin role for preview
 */
export const updateCrealoAdminRole = createAsyncThunk(
    "users/updateCrealoAdminRole",
    async ({ userId, role }: { userId: number; role: ROLES }, { rejectWithValue }) => {
        try {
            const result = await axios.put(import.meta.env.REACT_APP_API_ENDPOINT + `/company/members/${userId}/role`, {
                role,
            });
            return result.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export enum EnumCompanySettings {
    beneficiaryMailTemplateFR = "beneficiary_statement_email_template_fr",
    beneficiaryMailTemplateEN = "beneficiary_statement_email_template_en",
    termMailTemplateFR = "term_statement_email_template_fr",
    termMailTemplateEN = "term_statement_email_template_en",
    portalWelcomeEmailTemplateFR = "portal_welcome_email_template_fr",
    portalWelcomeEmailTemplateEN = "portal_welcome_email_template_en",
    acceptedPaymentMethods = "accepted_payment_methods",
    activeAlternativeCurrencies = "active_alternative_currencies",
    authorizedPublisherSeats = "authorized_publisher_seats",
    authorizedAgreementTemplatesLimit = "authorized_agreement_templates_limit",
    availableDistributors = "available_distributors",
    accountingSoftware = "accounting_software",
    customStatementWording = "custom_statement_wording",
    folioBasedByDefault = "folio_based_by_default",
    invoiceNumbering = "invoice_numbering",
    netStatementShowContributorCost = "net_statement_show_contributor_cost",
    preferredAccountingGroupMethod = "preferred_accounting_group_method",
    preferredTitleIdentifier = "preferred_title_identifier",
    showZeroPercentOnStatement = "show_zero_percent_on_statement",
    statementNumberingTemplate = "statement_numbering_template",
    termStatementEmailTemplate = "term_statement_email_template",
    usesAgreementValidationChain = "uses_agreement_validation_chain",
    usesAgreements = "uses_agreements",
    usesInterchannelCompensation = "uses_interchannel_compensation",
    usesIntertermCompensation = "uses_interterm_compensation",
    usesIntertitleCompensation = "uses_intertitle_compensation",
    usesMultipleBrands = "uses_multiple_brands",
    usesMultipleCurrencies = "uses_multiple_currencies",
    usesPortal = "uses_portal",
    usesPortalSocialProfile = "uses_portal_social_profile",
    usesPriceWithVat = "uses_price_with_vat",
    usesSimultaneousPeriod = "uses_simultaneous_period",
    usesStatementNumbering = "uses_statement_numbering",
    usesStatementSocialSecurityNumber = "uses_statement_social_security_number",
    usesAccountingWithChannelDispatch = "uses_accounting_with_channel_dispatch",
    usesCustomStatementTemplates = "uses_custom_statement_templates",
    usesInitialAdvance = "uses_initial_advance",
    showSoldQuantityInConsolidatedStatement = "show_sold_quantity_in_consolidated_statement",
    alwaysPayCollectiveAndOther = "always_pay_collective_and_other",
    usesMarketSegmentation = "uses_market_segmentation",
    usesMetadata = "uses_metadata",
    usesTransferTerm = "uses_transfer_term",
    representativeRoles = "representative_roles",
    usesAgreementsOnly = "uses_agreements_only",
    enforceMfa = "enforce_mfa",
    usesStock = "uses_stock",
    pressUsesMultipleChannels = "press_uses_multiple_channels",
    pressPreferredChannel = "press_preferred_channel",
    hideSalesZeroPercentTerm = "hide_sales_zero_percent_term",
    hideZeroLinesOnStatement = "hide_zero_lines_on_statement",
    hideZeroPercentTermsOnStatement = "hide_zero_percent_terms_on_statement",
    usesOverridenPriceOnSales = "uses_overriden_price_on_sales",
    analyticsLayout = "analytics_layout",
    usesAnalytics = "uses_analytics",
    usesUrssafDeclarations = "uses_urssaf_declarations",
    showAdvanceAmountInLettersOnAgreements = "show_advance_amount_in_letters_on_agreements",
    showInitialAdvanceAmountInLettersOnAgreements = "show_initial_advance_amount_in_letters_on_agreements",
    showRatesInLettersOnAgreements = "show_rates_in_letters_on_agreements",
    showBalanceInConsolidatedStatement = "show_balance_in_consolidated_statement",
    agreementsShowSignatoryJobTitle = "agreements_show_signatory_job_title",
    agreementsShowSignatureDate = "agreements_show_signature_date",
    usesIrcec = "uses_ircec",
    pressAgreementsDetachStatements = "press_agreements_detach_statements",
    usesWorkerMode = "uses_worker_mode",
    showTitleConsolidatedSales = "show_title_consolidated_sales",
    usesMinimumGuaranteedPayment = "uses_minimum_guaranteed_payment",
    accountingCustomExtraColumns = "accounting_custom_extra_columns",
    usesBeneficiaryProfileCompleteCheck = "uses_beneficiary_profile_complete_check",
    usesAccountantMailRemind = "uses_accountant_mail_remind",
    authorizedPublisherSeatsPerRole = "authorized_publisher_seats_per_role",
    ftpUsername = "ftp_username",
    accountingPnmLinebreak = "accounting_pnm_linebreak",
    analyticsRefreshContext = "analytics_refresh_context",
    usesTwentyPercentVat = "uses_twenty_percent_vat",
    accountingPieceAutoIncrement = "accounting_piece_auto_increment",
    usesScopedNotifications = "uses_scoped_notifications",
    usesCustomRoles = "uses_custom_roles",
    usesBeneficiaryDocumentsTracking = "uses_beneficiary_documents_tracking",
    beneficiaryDocumentsReminderTemplateFR = "beneficiary_documents_reminder_email_template_fr",
    beneficiaryDocumentsReminderTemplateEN = "beneficiary_documents_reminder_email_template_en",
}

export type Currencies = ICurrencyOption["code"];

export interface ICompany {
    id: number;
    name: string;
    description: string;
    address: string;
    city: string;
    country: string;
    countryCode: "fr-FR" | "fr-BE" | string;
    postalCode: string;
    siret: string;
    periodDuration: number;
    avatarUrl: string;
    created: Date;
    modified: Date;
    bankAccountName: string;
    iban: string;
    swift: string;
    showStockMovements?: number;
    useCompensationByDefault?: number;
    useStatementNumbering?: number;
    settings: { id: number; name: string; content: any }[];
    phoneNumber: string;
    apeCode: string;
    activityType: string;
    /**
     * Type of distributor: Diffuseurs d'œuvres, Tiers habilités conventionnés, Diffuseurs commerçants d'arts
     */
    distributorType: number;
    signature: string;
    sendPictureStatus?: TFetchStatus;
    publisherType?: TPortalType;
    defaultPackageCalculationMethod: "folio" | "amount";
    currency: Currencies;
    /**
     * Identification number for intra-community VAT (european)
     */
    intraCommunityVAT: string;
    /**
     * Country-specific banking fields (e.g. sort_code + account_number for UK).
     * Replaces IBAN/BIC for non-SEPA countries.
     */
    bankingMetadata?: Record<string, string> | null;
}

export type MainPortalType = SubsetOf<TPortalType, "press" | "book" | "audiovisual">;

export interface CompanyLightInfo {
    active: number;
    avatarUrl: string | null;
    companyId: number;
    name: string;
    syncToken: string | null;
    userId: number;
}

export type AccountRole = "none" | "accountant" | "rights_manager" | "stock_manager" | "view-only" | "owner";
export interface APIKey {
    id?: number;
    userId?: number;
    email?: string;
    keyScope: string;
    name: string;
    sample?: string;
    key?: string;
    createdAt: Date | string | null;
    modifiedAt: Date | string | null;
    expiryDate: Date | string | null;
}

export interface IUserData {
    id?: number;
    uuid: string;
    firstname: string;
    lastname: string;
    email?: string;
    description: string | null;
    avatarUrl: string | null;
    type: "publisher" | "beneficiary" | "admin" | "superadmin";
    role: ROLES;
    isVerified: boolean;
    hasAcceptedToS: boolean;
    created?: number | Date;
    modified?: number | Date;
    companies: CompanyLightInfo[];
    jobTitle: string | null;
    permissions?: { action: string; subject: string; conditions?: Record<string, any> }[] | null;
}

//TODO: implement real options for another settings tabs

export type LangFile = { [key: string]: string | boolean | { [key: string]: any } } | any;
export interface SettingsState {
    statementsSettings: {
        cumulativeSales: boolean;
        stocks: boolean;
        physicalChannels: boolean;
        digitalChannels: boolean;
        rightTransferChannels: boolean;
        socialOrgsChannels: boolean;
    };
    accountSettings: {
        language: "fr" | "en" | string;
        languageSetByUser?: boolean;
    };
    notificationsSettings: object;
    periodSettings: object;
    memberSettings: object;
    companySettings: ICompany | undefined;
    billingSettings: object;
    statusQuery: {
        putCompanySettingsStatus: TFetchStatus;
    };
    crispHash: string;
    features?: CountryFeatures;
    i18nKeys?: LangFile;
    apiKeys: APIKey[];
    userData: IUserData | null;
    userRole: ROLES;
    availableStatementI18nKeys: Record<string, any>;
    availableStatementI18nKeysEn: Record<string, any>;
    currentPortal: TPortalType | null;
    showNavDrawer: boolean;
    showTitleDrawer: boolean;
    showNotificationsDrawer: boolean;
    showSettingsTab: boolean;
    companyInfoStatus: TFetchStatus;
    /**
     * If user (Crealo only atm) is on role preview mode
     */
    rolePreview: { role: ROLES; label: string } | undefined;
}

const localStorageRaw = typeof localStorage !== "undefined" ? localStorage.getItem("settings") : null;
const localStorageSettings: SettingsState = JSON.parse(localStorageRaw ?? "{}");

const initialState: SettingsState = {
    statementsSettings: {
        cumulativeSales: true,
        stocks: true,
        physicalChannels: true,
        digitalChannels: true,
        rightTransferChannels: true,
        socialOrgsChannels: true,
    },
    accountSettings: {
        language: localStorageSettings?.accountSettings?.language || "",
        languageSetByUser: localStorageSettings?.accountSettings?.languageSetByUser || false,
    },
    notificationsSettings: {},
    periodSettings: {},
    memberSettings: {},
    companySettings: undefined,
    billingSettings: {},
    statusQuery: {
        putCompanySettingsStatus: "idle",
    },
    crispHash: "",
    features: undefined,
    i18nKeys: undefined,
    apiKeys: [],
    userData: null,
    userRole: ROLES.NONE,
    availableStatementI18nKeys: {},
    availableStatementI18nKeysEn: {},
    currentPortal: null,
    showNavDrawer: false,
    showTitleDrawer: false,
    showNotificationsDrawer: false,
    showSettingsTab: false,
    companyInfoStatus: "idle",
    rolePreview: undefined,
};

export const settingsSlice = createSlice({
    name: "settings",
    initialState,
    reducers: {
        setLocalSettings(state, { payload, type }: { payload: SettingsState; type: string }) {
            // TODO => Add the other fields
            state.accountSettings = payload.accountSettings;
            const settingsJson = JSON.stringify({
                accountSettings: payload.accountSettings,
            });
            localStorage.setItem("settings", settingsJson);
        },
        setCurrentPortal(state, action) {
            state.currentPortal = action.payload;
        },
        toggleNavDrawer: (state, action) => {
            state.showNavDrawer = action.payload;
        },
        toggleTitleDrawer: (state, action) => {
            state.showTitleDrawer = action.payload;
        },
        toggleNotificationsDrawer: (state, action) => {
            state.showNotificationsDrawer = action.payload;
        },
        toggleSettingsTab: (state, action) => {
            state.showSettingsTab = action.payload;
        },
        updateUserData: (state, action) => {
            const newData = action.payload;
            state.userData = { ...state.userData, ...newData };
        },
    },
    //TODO: create new store to receive html document
    extraReducers(builder) {
        builder.addCase(fetchSettings.fulfilled, (state, action) => {
            state = action.payload;
        });
        builder.addCase(fetchSettings.rejected, (state, { payload }) => {
            toastApiError(payload, "ServerError.InternalServerError");
        });
        builder.addCase(fetchCompanyInfo.pending, (state, action) => {
            state.companyInfoStatus = "pending";
        });
        builder.addCase(fetchCompanyInfo.fulfilled, (state, action) => {
            action.payload.company.settings = action.payload.company.settings.map(
                (s: { name: string; content: any }) => {
                    if (s.name.includes("email_template")) {
                        s.content = parseEmailTemplateContent(s.content);
                    }
                    return s;
                },
            );
            const company = action.payload.company;
            if (typeof company.bankingMetadata === "string") {
                try {
                    company.bankingMetadata = JSON.parse(company.bankingMetadata);
                } catch {
                    company.bankingMetadata = null;
                }
            }
            state.companySettings = company;
            state.crispHash = action.payload.hash;
            state.availableStatementI18nKeys = action.payload.i18nKeys; // Legacy added by Josh when working on customizable statement wording - to be migrated
            state.availableStatementI18nKeysEn = action.payload.i18nKeysEn;
            state.features = action.payload.features;
            state.i18nKeys = action.payload.i18nKeys;
            state.companyInfoStatus = "fulfilled";
        });
        builder.addCase(fetchCompanyInfo.rejected, (state, { payload }) => {
            toastApiError(payload, "ServerError.InternalServerError");
            state.companyInfoStatus = "rejected";
        });

        builder.addCase(fetchMe.fulfilled, (state, action) => {
            // action.payload.companies = action.payload.companies.slice(0, 1)
            const user = action.payload;
            state.userData = user;
            state.userRole = user.role;
            const isPreviewingRole = user.type === "admin" && user.role !== ROLES.OWNER;
            if (isPreviewingRole) {
                state.rolePreview = { role: user.role, label: getUserRolesLabel(user.role) };
            }
        });
        builder.addCase(fetchMe.rejected, (state, { payload }) => {
            toastApiError(payload, "ServerError.InternalServerError");
        });

        builder.addCase(fetchMeBP.fulfilled, (state, action) => {
            // action.payload.companies = action.payload.companies.slice(0, 1)
            state.userData = action.payload;
        });
        builder.addCase(fetchMeBP.rejected, (state, { payload }) => {
            toastApiError(payload, "ServerError.InternalServerError");
        });

        builder.addCase(patchCompanyInfo.pending, () => {});
        builder.addCase(patchCompanyInfo.fulfilled, (state, action) => {
            toast.success(`${t("Settings.UpdateCompanyInfoSuccess")}`);
            state.companySettings = { ...state.companySettings, ...action.payload };

            const companyId = action.meta.arg.id;
            const companyName = action.meta.arg.name;
            const updatedUserCompanies = (state.userData?.companies ?? []).map((company) => {
                if (company.companyId === companyId) {
                    return { ...company, name: companyName };
                } else {
                    return company;
                }
            });
            state.userData = { ...(state.userData as IUserData), companies: updatedUserCompanies };
        });
        builder.addCase(patchCompanyInfo.rejected, (state, action) => {
            if (isAppUnavailablePayload(action.payload)) return;
            const message = (action.payload as any)?.message;
            const status = (action.payload as any)?.status;
            const details = (action.payload as any)?.data;
            const translation = translationsWithFallback(message, "ServerError.InternalServerError");

            if (message === "ServerError.InvalidCompanyBankInfos") {
                if (status === 422) {
                    // bankingMetadata validation failed — data was NOT saved, show specific field error
                    toast.error(details || `${translation}`, { autoClose: 5000 });
                } else {
                    // IBAN/BIC invalid but data WAS saved (417) — keep warning
                    toast.warning(`${translation}`, { autoClose: 5000 });
                    // Companies info in userData not updated immediately so we need to force update
                    // for the companies in the switcher to be coherent with changes
                    const companyId = action.meta.arg.id;
                    const companyName = action.meta.arg.name;
                    const updatedUserCompanies = (state.userData?.companies ?? []).map((company) => {
                        if (company.companyId === companyId) {
                            return { ...company, name: companyName };
                        } else {
                            return company;
                        }
                    });
                    state.userData = { ...(state.userData as IUserData), companies: updatedUserCompanies };
                }
            } else {
                toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
            }
        });

        builder.addCase(putBankInfo.pending, () => {});
        builder.addCase(putBankInfo.fulfilled, () => {
            toast.success(`${t("Settings.UpdateBankInfoSuccess")}`);
        });
        builder.addCase(putBankInfo.rejected, (_, { payload }) => {
            toastApiError(payload, "ServerError.InternalServerError");
        });

        builder.addCase(putCompanySettings.pending, (state, action) => {
            state.statusQuery.putCompanySettingsStatus = "pending";
        });
        builder.addCase(putCompanySettings.fulfilled, (state, action) => {
            if (state.companySettings) {
                const settings = state.companySettings.settings;
                const payloadArray = action.meta.arg;
                settings.map((s) => {
                    const template = payloadArray.find((e) => e.settingsName === s.name);
                    if (template) {
                        // Template names carry a language suffix (`_fr`, `_en`): an exact match left them as raw JSON strings
                        if (template.settingsName.includes("email_template")) {
                            s.content = parseEmailTemplateContent(template.settingsValue);
                        } else {
                            // For the HTML
                            s.content = template.settingsValue;
                        }
                    }
                    return s;
                });
                // Add new settings that didn't exist in the store yet (first save)
                // Use negative temp IDs to avoid collisions with real DB auto-increment IDs (always positive)
                let nextTempId = -1;
                payloadArray.forEach((template) => {
                    const exists = settings.some((s) => s.name === template.settingsName);
                    if (!exists) {
                        settings.push({
                            id: nextTempId--,
                            name: template.settingsName,
                            // Email templates are stored as JSON and read as objects by the composer
                            content: template.settingsName.includes("email_template")
                                ? parseEmailTemplateContent(template.settingsValue)
                                : template.settingsValue,
                        });
                    }
                });
                state.companySettings.settings = settings;
            }
            // Analytics page : don't send a toast at each layout changes (moving / resize a graph).
            const isAnalyticsUpdate = action.meta.arg[0].settingsName === "analytics_layout";
            !isAnalyticsUpdate && toast.success(`${t("Settings.SettingsSaved")}`);
            state.statusQuery.putCompanySettingsStatus = "succeeded";
        });
        builder.addCase(putCompanySettings.rejected, (state, { payload }) => {
            toastApiError(payload, "ServerError.InternalServerError");
            state.statusQuery.putCompanySettingsStatus = "failed";
        });

        builder.addCase(sendCompanySignature.fulfilled, (state) => {
            toast.success(`${t("Beneficiars.AddPictureSuccess")}`);
            state.companySettings!.sendPictureStatus = "succeeded";
        });
        builder.addCase(sendCompanySignature.pending, (state) => {
            state.companySettings!.sendPictureStatus = "pending";
        });
        builder.addCase(sendCompanySignature.rejected, (state, { payload }) => {
            toastApiError(payload, "ServerError.InternalServerError");
            state.companySettings!.sendPictureStatus = "failed";
        });

        // API Keys

        builder.addCase(fetchAPIKeys.fulfilled, (state, action) => {
            state.apiKeys = action.payload;
        });
        builder.addCase(fetchAPIKeys.rejected, (state, { payload }) => {
            toastApiError(payload, "ServerError.InternalServerError");
        });

        builder.addCase(deleteOneAPIKey.fulfilled, (state, action) => {
            state.apiKeys = state.apiKeys.filter((key) => key.id !== action.payload.id);
            toast.success(`${t("Settings.APIKey.SuccessfullyDeleted")}`);
        });
        builder.addCase(deleteOneAPIKey.rejected, (state, { payload }) => {
            toastApiError(payload, "ServerError.InternalServerError");
        });

        builder.addCase(generateAPIKey.fulfilled, (state, action) => {
            const created = action.payload;
            if (!created || typeof created !== "object") return;

            const { key: secret, ...storedKey } = created;
            state.apiKeys = [...state.apiKeys, storedKey];
            if (typeof secret === "string" && secret.length > 0) {
                toast.success(`${t("Settings.APIKey.SuccessfullyCreated")}`);
            }
        });
        builder.addCase(generateAPIKey.rejected, (state, { payload }) => {
            toastApiError(payload, "ServerError.InternalServerError");
        });

        builder.addCase(putMeVerifiedOrAcceptedTos.fulfilled, (state, action) => {
            state.userData = { ...state.userData, ...action.payload };
            toast.success(`${t("Verification.SuccessfullyVerified")}`);
        });
        builder.addCase(putMeVerifiedOrAcceptedTos.rejected, (state, { payload }) => {
            // const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            // toast.error(`${ translation }`);
        });

        builder.addCase(putMeVerifiedOrAcceptedTosBP.fulfilled, (state, action) => {
            state.userData = { ...state.userData, ...action.payload };
            toast.success(`${t("Verification.SuccessfullyVerified")}`);
        });
        builder.addCase(putMeVerifiedOrAcceptedTosBP.rejected, (state, { payload }) => {
            // const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            // toast.error(`${ translation }`);
        });
        builder.addCase(updateMe.fulfilled, (state, action) => {
            toast.success(`${t("Settings.SignatoryTitleSuccess")}`);
            state.userData = { ...state.userData, ...action.payload.data };
        });
        builder.addCase(updateMe.rejected, (state, { payload }) => {
            toastApiError(payload, "ServerError.InternalServerError");
        });
        builder.addCase(updateCrealoAdminRole.rejected, (state, { payload }) => {
            toastApiError(payload, "ServerError.InternalServerError");
        });
        builder.addCase(updateCrealoAdminRole.fulfilled, (state, action) => {
            const { role } = action.payload;
            state.userData = { ...state.userData, role } as IUserData;
            state.userRole = role;
            if (role !== ROLES.OWNER) {
                state.rolePreview = { role, label: getUserRolesLabel(role) };
            } else {
                state.rolePreview = undefined;
            }
            toast.success("Le rôle choisi vous a bien été attribué");
        });
    },
});

export default settingsSlice.reducer;
export const {
    setLocalSettings,
    setCurrentPortal,
    toggleNavDrawer,
    toggleTitleDrawer,
    toggleNotificationsDrawer,
    toggleSettingsTab,
    updateUserData,
} = settingsSlice.actions;

// Closing period state ---------

export interface ClosingPeriodState {
    closing: boolean;
}

const initialStateClosingPeriod: ClosingPeriodState = {
    closing: false,
};

export const getClosePeriod = createAsyncThunk("closingPeriod/get", async () => {
    return store.getState().closingPeriod.closing;
});

export const closingPeriod = createSlice({
    name: "closingPeriod",
    initialState: initialStateClosingPeriod,
    reducers: {
        setClosingPeriod(state, { payload }: PClosing) {
            state.closing = payload.closing;
        },
    },
});

export const closingPeriodReducer = closingPeriod.reducer;
export const { setClosingPeriod } = closingPeriod.actions;

/** Body `message` of a planned update. Distinct from a 503 period close. */
export { APP_UNAVAILABLE_MESSAGE };

export type AppMaintenanceNotice = {
    startsAt: string;
    endsAt: string;
    title: string;
    description: string;
};

export type AppMaintenanceState = {
    unavailable: boolean;
    /** Crealo admin left the screen and may open the maintenance tab. */
    bypassed: boolean;
    startsAt: string | null;
    endsAt: string | null;
    title: string | null;
    description: string | null;
    /** Next window starting within 48 hours. Null when none is announced. */
    soon: AppMaintenanceNotice | null;
};

const emptyMaintenance = (): AppMaintenanceState => ({
    unavailable: false,
    bypassed: false,
    startsAt: null,
    endsAt: null,
    title: null,
    description: null,
    soon: null,
});

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null;

/** ISO bound from the AppUnavailable payload. Absent or blank stays null. */
const readMaintenanceBound = (data: unknown, key: "startsAt" | "endsAt"): string | null => {
    if (!isRecord(data)) return null;
    const value = data[key];
    return typeof value === "string" && value.length > 0 ? value : null;
};

const readMaintenanceText = (data: unknown, key: "title" | "description"): string | null => {
    if (!isRecord(data)) return null;
    const value = data[key];
    return typeof value === "string" && value.trim().length > 0 ? value : null;
};

/** Body of a period close. A bare 503 from the load balancer is not one. */
const CLOSING_PERIOD_MESSAGE = "ServerError.CompanyBusyProcessingData";

const MAINTENANCE_STORAGE_KEY = "crealo.appMaintenance";

/** The end instant is exclusive: once it is reached, the screen must leave. */
const maintenanceWindowHasEnded = (endsAt: string | null): boolean => {
    if (!endsAt) return false;
    const end = new Date(endsAt).getTime();
    return Number.isFinite(end) && Date.now() >= end;
};

const maintenanceWindowHasStarted = (startsAt: string | null): boolean => {
    if (!startsAt) return false;
    const start = new Date(startsAt).getTime();
    return Number.isFinite(start) && Date.now() >= start;
};

/** Network error or a gateway status with no maintenance or closing body. */
const isBackendUnreachable = (error: unknown): boolean => {
    if (!isRecord(error)) return false;
    const config = error.config;
    const url = isRecord(config) && typeof config.url === "string" ? config.url : "";
    const apiEndpoint = import.meta.env.REACT_APP_API_ENDPOINT;
    if (typeof apiEndpoint !== "string" || apiEndpoint.length === 0 || !url.startsWith(apiEndpoint)) return false;
    if (!isRecord(error.response)) return true;
    const status = error.response.status;
    return status === 502 || status === 503 || status === 504;
};

const rememberMaintenanceWindow = (signal: MaintenanceSignal | null) => {
    try {
        if (!signal?.startsAt || !signal.endsAt || maintenanceWindowHasEnded(signal.endsAt)) {
            localStorage.removeItem(MAINTENANCE_STORAGE_KEY);
            return;
        }
        localStorage.setItem(
            MAINTENANCE_STORAGE_KEY,
            JSON.stringify({
                startsAt: signal.startsAt,
                endsAt: signal.endsAt,
                title: signal.title,
                description: signal.description,
            }),
        );
    } catch {
        // Ignore a blocked localStorage.
    }
};

const readRememberedMaintenance = (): MaintenanceSignal | null => {
    try {
        const raw = localStorage.getItem(MAINTENANCE_STORAGE_KEY);
        if (!raw) return null;
        const parsed: unknown = JSON.parse(raw);
        const startsAt = readMaintenanceBound(parsed, "startsAt");
        const endsAt = readMaintenanceBound(parsed, "endsAt");
        if (!startsAt || !endsAt) return null;
        return {
            startsAt,
            endsAt,
            title: readMaintenanceText(parsed, "title"),
            description: readMaintenanceText(parsed, "description"),
        };
    } catch {
        return null;
    }
};

/** Last announced window, once it has started and before it has ended. */
const rememberedActiveWindow = (): MaintenanceSignal | null => {
    const signal = readRememberedMaintenance();
    if (!signal || !maintenanceWindowHasStarted(signal.startsAt) || maintenanceWindowHasEnded(signal.endsAt)) {
        return null;
    }
    return signal;
};

const readExposedHeader = (headers: unknown, name: string): string | undefined => {
    if (!isRecord(headers)) return undefined;
    const direct = headers[name];
    if (typeof direct === "string") return direct;
    const getter = headers.get;
    if (typeof getter !== "function") return undefined;
    const value = getter.call(headers, name);
    return typeof value === "string" ? value : undefined;
};

const appMaintenance = createSlice({
    name: "appMaintenance",
    initialState: emptyMaintenance(),
    reducers: {
        setAppUnavailable(state, { payload }: { payload: Omit<AppMaintenanceState, "bypassed" | "soon"> }) {
            state.unavailable = payload.unavailable;
            state.startsAt = payload.startsAt;
            state.endsAt = payload.endsAt;
            state.title = payload.title;
            state.description = payload.description;
        },
        bypassAppMaintenance(state) {
            state.bypassed = true;
        },
        clearAppMaintenance() {
            return emptyMaintenance();
        },
        setMaintenanceSoon(state, { payload }: { payload: AppMaintenanceNotice | null }) {
            state.soon = payload;
        },
    },
});

export const appMaintenanceReducer = appMaintenance.reducer;
export const { setAppUnavailable, bypassAppMaintenance, clearAppMaintenance, setMaintenanceSoon } =
    appMaintenance.actions;

type MaintenanceSignal = {
    startsAt: string | null;
    endsAt: string | null;
    title: string | null;
    description: string | null;
};

/** One writer for the screen. `null` or an ended window lifts it. An open window shows it. */
export function applyMaintenanceSignal(signal: MaintenanceSignal | null) {
    return (dispatch: typeof store.dispatch) => {
        if (!signal || maintenanceWindowHasEnded(signal.endsAt)) {
            rememberMaintenanceWindow(null);
            dispatch(clearAppMaintenance());
            return;
        }
        rememberMaintenanceWindow(signal);
        dispatch(
            setAppUnavailable({
                unavailable: true,
                startsAt: signal.startsAt,
                endsAt: signal.endsAt,
                title: signal.title,
                description: signal.description,
            }),
        );
        dispatch(setMaintenanceSoon(null));
    };
}

/** Active window announced to Crealo admins, whose requests are not rejected. */
const syncMaintenanceFromResponse = (response: {
    headers?: unknown;
    config?: { url?: string };
    request?: { responseURL?: string };
}) => {
    const url = response.request?.responseURL || response.config?.url || "";
    const apiEndpoint = import.meta.env.REACT_APP_API_ENDPOINT;
    if (typeof apiEndpoint !== "string" || apiEndpoint.length === 0 || !url.startsWith(apiEndpoint)) return;
    if (url.includes("/adminPortal/maintenance")) return;
    const raw = readExposedHeader(response.headers, "x-app-maintenance");
    if (typeof raw === "string" && raw.length > 0) {
        try {
            const parsed: unknown = JSON.parse(decodeURIComponent(raw));
            store.dispatch(
                applyMaintenanceSignal({
                    startsAt: readMaintenanceBound(parsed, "startsAt"),
                    endsAt: readMaintenanceBound(parsed, "endsAt"),
                    title: readMaintenanceText(parsed, "title"),
                    description: readMaintenanceText(parsed, "description"),
                }),
            );
        } catch {
            // Ignore a malformed maintenance header.
        }
        return;
    }
    // Absence on a random call must not lift the screen: those calls were flipping it.
    if (isCompanyInfoUrl(url, apiEndpoint) && store.getState().appMaintenance.unavailable) {
        store.dispatch(applyMaintenanceSignal(null));
    }
    syncMaintenanceSoon(response.headers);
};

const readMaintenanceNotice = (parsed: unknown): AppMaintenanceNotice | null => {
    const startsAt = readMaintenanceBound(parsed, "startsAt");
    const endsAt = readMaintenanceBound(parsed, "endsAt");
    if (!startsAt || !endsAt || maintenanceWindowHasEnded(endsAt)) return null;
    return {
        startsAt,
        endsAt,
        title: readMaintenanceText(parsed, "title") ?? "",
        description: readMaintenanceText(parsed, "description") ?? "",
    };
};

const syncMaintenanceSoon = (headers: unknown) => {
    const raw = readExposedHeader(headers, "x-app-maintenance-soon");
    if (typeof raw !== "string" || raw.length === 0) {
        if (store.getState().appMaintenance.soon) store.dispatch(setMaintenanceSoon(null));
        if (!store.getState().appMaintenance.unavailable) rememberMaintenanceWindow(null);
        return;
    }
    try {
        const notice = readMaintenanceNotice(JSON.parse(decodeURIComponent(raw)));
        store.dispatch(setMaintenanceSoon(notice));
        if (!store.getState().appMaintenance.unavailable) rememberMaintenanceWindow(notice);
    } catch {
        store.dispatch(setMaintenanceSoon(null));
    }
};
